<template>
    <div class="fixed inset-0 z-[130] flex items-center justify-center p-4">
        <div class="foxy-modal-overlay !absolute" @click="$emit('close')"></div>
        <div class="foxy-modal relative z-10 max-w-2xl !border-0 p-5">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <h2 class="text-xl font-bold text-foxy-text">{{ library.name }}</h2>
                    <p class="foxy-muted mt-1 text-sm">Додавайте або видаляйте книги з добірки.</p>
                </div>
                <button
                    type="button"
                    class="rounded-lg p-2 text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text"
                    aria-label="Закрити"
                    @click="$emit('close')"
                >
                    ✕
                </button>
            </div>

            <div class="mt-5">
                <label class="mb-2 block text-sm font-semibold text-foxy-text" for="library-book-search">Пошук книги</label>
                <input
                    id="library-book-search"
                    v-model="bookQuery"
                    type="search"
                    class="foxy-input"
                    placeholder="Почніть вводити назву книги або автора…"
                    @input="queueSearch"
                />

                <div v-if="bookQuery.trim().length < 2" class="foxy-muted mt-2 text-xs">Введіть щонайменше 2 символи</div>
                <div v-else-if="searching" class="foxy-muted mt-3 flex items-center gap-2 text-sm">
                    <span class="h-4 w-4 animate-spin rounded-full border-2 border-foxy-border border-t-foxy-action" aria-hidden="true"></span>
                    <span>Шукаємо…</span>
                </div>
                <div
                    v-else-if="completedQuery === bookQuery.trim() && !searchResults.length"
                    class="mt-3 text-sm font-medium text-foxy-text-secondary"
                >
                    Нічого не знайдено
                </div>
                <div v-if="searchResults.length" class="mt-3 max-h-56 space-y-2 overflow-auto">
                    <div
                        v-for="book in searchResults"
                        :key="`search-${book.id}`"
                        class="flex items-center justify-between gap-3 rounded-xl border border-foxy-border bg-foxy-elevated p-2"
                    >
                        <div class="flex min-w-0 items-center gap-3">
                            <img
                                :src="resolveBookCover(book.cover)"
                                class="h-16 w-11 shrink-0 rounded bg-foxy-elevated object-cover"
                                alt=""
                                @error="onCoverError"
                            />
                            <div class="min-w-0">
                                <div class="truncate font-semibold text-foxy-text">{{ book.title }}</div>
                                <div class="foxy-muted truncate text-sm">{{ book.author || 'Автор невідомий' }}</div>
                            </div>
                        </div>
                        <button
                            v-if="!isInLibrary(book)"
                            type="button"
                            class="foxy-btn-primary shrink-0 !rounded-lg !px-3 !py-2 !text-sm !font-semibold"
                            @click="addBook(book)"
                        >
                            Додати
                        </button>
                        <button
                            v-else
                            type="button"
                            class="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-foxy-error hover:bg-foxy-error/10"
                            @click="removeBook(book)"
                        >
                            Видалити
                        </button>
                    </div>
                </div>
            </div>

            <div class="mt-6">
                <div class="mb-2 flex items-center justify-between gap-3">
                    <h3 class="text-sm font-bold text-foxy-text">Книги у добірці</h3>
                    <span v-if="!loadingBooks && !loadError" class="foxy-muted text-xs">Усього: {{ pagination.total }}</span>
                </div>
                <div v-if="loadingBooks" class="foxy-muted text-sm">Завантаження…</div>
                <p v-else-if="loadError" class="text-sm font-medium text-foxy-error">{{ loadError }}</p>
                <div v-else-if="collectionBooks.length" class="max-h-64 space-y-2 overflow-auto">
                    <div
                        v-for="book in collectionBooks"
                        :key="`library-book-${book.id}`"
                        class="flex items-center justify-between gap-3 rounded-xl border border-foxy-border bg-foxy-elevated p-2"
                    >
                        <div class="flex min-w-0 items-center gap-3">
                            <img
                                :src="resolveBookCover(book.cover)"
                                class="h-14 w-10 shrink-0 rounded bg-foxy-elevated object-cover"
                                alt=""
                                @error="onCoverError"
                            />
                            <div class="min-w-0">
                                <div class="truncate font-semibold text-foxy-text">{{ book.title }}</div>
                                <div v-if="book.author" class="foxy-muted truncate text-sm">{{ book.author }}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-foxy-error hover:bg-foxy-error/10"
                            @click="removeBook(book)"
                        >
                            Видалити
                        </button>
                    </div>
                </div>
                <p v-else class="foxy-muted text-sm">У добірці ще немає книг.</p>

                <div v-if="!loadingBooks && !loadError && pagination.lastPage > 1" class="mt-3 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        class="foxy-btn-secondary !rounded-lg !border-0 !px-3 !py-2 !text-sm !font-semibold"
                        :disabled="!hasPreviousPage"
                        @click="loadCollectionBooks(pagination.currentPage - 1)"
                    >
                        Попередня
                    </button>
                    <span class="foxy-muted text-xs">Сторінка {{ pagination.currentPage }} з {{ pagination.lastPage }}</span>
                    <button
                        type="button"
                        class="foxy-btn-secondary !rounded-lg !border-0 !px-3 !py-2 !text-sm !font-semibold"
                        :disabled="!hasNextPage"
                        @click="loadCollectionBooks(pagination.currentPage + 1)"
                    >
                        Наступна
                    </button>
                </div>
            </div>

            <div class="mt-5 flex justify-end gap-2">
                <a v-if="showEditLink" :href="library.edit_url" class="foxy-btn-primary !rounded-lg !px-4 !py-2 !text-base !font-semibold"
                    >Редагувати інформацію</a
                >
                <button type="button" class="foxy-btn-secondary !rounded-lg !border-0 !px-4 !py-2 !text-base !font-semibold" @click="$emit('close')">
                    Закрити
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'LibraryBooksManager',
    props: {
        library: {
            type: Object,
            required: true,
        },
        showEditLink: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            collectionBooks: [],
            bookQuery: '',
            searchResults: [],
            searchTimer: null,
            searchRequest: 0,
            collectionRequest: 0,
            searching: false,
            completedQuery: '',
            loadingBooks: false,
            loadError: '',
            pagination: {
                total: 0,
                currentPage: 1,
                lastPage: 1,
                perPage: 25,
            },
            paginationLinks: {
                prev: null,
                next: null,
            },
        };
    },
    watch: {
        'library.id'(next, previous) {
            if (Number(next) === Number(previous)) return;
            this.resetCollectionContext();
            return this.loadCollectionBooks(1);
        },
    },
    computed: {
        hasPreviousPage() {
            return this.pagination.currentPage > 1 && Boolean(this.paginationLinks.prev);
        },
        hasNextPage() {
            return this.pagination.currentPage < this.pagination.lastPage && Boolean(this.paginationLinks.next);
        },
    },
    mounted() {
        this.loadCollectionBooks();
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
        this.searchRequest += 1;
        this.invalidateCollectionRequests();
    },
    methods: {
        resolveBookCover(cover) {
            return cover || '/images/placeholders/book-cover.svg';
        },
        onCoverError(event) {
            const fallback = '/images/placeholders/book-cover.svg';
            if (event && event.target && !String(event.target.src || '').includes(fallback)) {
                event.target.src = fallback;
            }
        },
        isInLibrary(book) {
            return book?.is_in_collection === true || Number(book?.is_in_collection) === 1;
        },
        emitBooksChanged(collectionId, mutationType, bookId) {
            this.$emit('books-changed', {
                libraryId: Number(collectionId),
                booksCount: this.pagination.total,
                mutationType,
                bookId: Number(bookId),
            });
        },
        invalidateCollectionRequests() {
            this.collectionRequest += 1;
            this.loadingBooks = false;
        },
        isCurrentCollectionRequest(requestId, collectionId) {
            return requestId === this.collectionRequest && Number(this.library.id) === Number(collectionId);
        },
        resetCollectionContext() {
            this.invalidateCollectionRequests();
            this.searchRequest += 1;
            this.collectionBooks = [];
            this.searchResults = [];
            this.completedQuery = '';
            this.searching = false;
            this.loadError = '';
            this.pagination = {
                total: 0,
                currentPage: 1,
                lastPage: 1,
                perPage: 25,
            };
            this.paginationLinks = { prev: null, next: null };
        },
        async fetchCollectionPage(page, collectionId) {
            const { data } = await axios.get(`/api/account/collections/${collectionId}/books`, {
                params: { page },
            });

            return data || {};
        },
        applyCollectionPage(data) {
            const meta = data.meta || {};
            this.collectionBooks = data.books || [];
            this.pagination = {
                total: Number(meta.total || 0),
                currentPage: Number(meta.current_page || 1),
                lastPage: Math.max(1, Number(meta.last_page || 1)),
                perPage: Number(meta.per_page || 25),
            };
            this.paginationLinks = {
                prev: data.links?.prev || null,
                next: data.links?.next || null,
            };
        },
        async loadCollectionBooks(page = 1) {
            const collectionId = Number(this.library.id);
            const requestId = ++this.collectionRequest;
            this.loadingBooks = true;
            this.loadError = '';
            try {
                let data = await this.fetchCollectionPage(page, collectionId);
                if (!this.isCurrentCollectionRequest(requestId, collectionId)) return false;
                const meta = data.meta || {};
                const lastPage = Math.max(1, Number(meta.last_page || 1));
                const currentPage = Number(meta.current_page || page);

                if ((data.books || []).length === 0 && Number(meta.total || 0) > 0 && currentPage > lastPage) {
                    data = await this.fetchCollectionPage(lastPage, collectionId);
                    if (!this.isCurrentCollectionRequest(requestId, collectionId)) return false;
                }

                this.applyCollectionPage(data);

                return true;
            } catch (error) {
                if (!this.isCurrentCollectionRequest(requestId, collectionId)) return false;
                this.loadError = error.response?.data?.message || 'Не вдалося завантажити книги добірки.';
                this.$emit('notification', this.loadError, 'error');

                return false;
            } finally {
                if (this.isCurrentCollectionRequest(requestId, collectionId)) {
                    this.loadingBooks = false;
                }
            }
        },
        queueSearch() {
            clearTimeout(this.searchTimer);
            const query = this.bookQuery.trim();
            const requestId = ++this.searchRequest;
            this.completedQuery = '';

            if (query.length < 2) {
                this.searchResults = [];
                this.searching = false;
                return;
            }
            this.searchResults = [];
            this.searching = true;
            this.searchTimer = setTimeout(() => this.searchBooks(query, requestId), 350);
        },
        async searchBooks(query, requestId) {
            try {
                const { data } = await axios.get('/api/account/books/search', {
                    params: { q: query, collection_id: this.library.id },
                });
                if (requestId === this.searchRequest) {
                    this.searchResults = data.books || [];
                    this.completedQuery = query;
                }
            } catch (error) {
                if (requestId === this.searchRequest) {
                    this.searchResults = [];
                    this.completedQuery = '';
                    this.$emit('notification', error.response?.data?.message || 'Не вдалося виконати пошук.', 'error');
                }
            } finally {
                if (requestId === this.searchRequest) {
                    this.searching = false;
                }
            }
        },
        async addBook(book) {
            if (this.isInLibrary(book)) return;
            const collectionId = Number(this.library.id);
            this.invalidateCollectionRequests();
            try {
                await axios.post(`/api/account/collections/${collectionId}/books`, { book_slug: book.slug });
                if (Number(this.library.id) !== collectionId) return;
                await this.refreshAfterMutation(collectionId, 'add', book.id);
            } catch (error) {
                if (Number(this.library.id) !== collectionId) return;
                this.$emit('notification', error.response?.data?.message || 'Не вдалося додати книгу.', 'error');
            }
        },
        async removeBook(book) {
            const collectionId = Number(this.library.id);
            this.invalidateCollectionRequests();
            try {
                await axios.delete(`/api/account/collections/${collectionId}/books/${book.id}`);
                if (Number(this.library.id) !== collectionId) return;
                await this.refreshAfterMutation(collectionId, 'remove', book.id);
            } catch (error) {
                if (Number(this.library.id) !== collectionId) return;
                this.$emit('notification', error.response?.data?.message || 'Не вдалося видалити книгу.', 'error');
            }
        },
        async refreshAfterMutation(collectionId, mutationType, bookId) {
            const loaded = await this.loadCollectionBooks(this.pagination.currentPage);
            if (Number(this.library.id) !== Number(collectionId)) return;
            const query = this.bookQuery.trim();
            if (query.length >= 2) {
                await this.searchBooks(query, ++this.searchRequest);
            }
            if (loaded) {
                this.emitBooksChanged(collectionId, mutationType, bookId);
            }
        },
    },
};
</script>
